/**
 * Datetime parsing/formatting helpers — fixes the "UTC time displayed as
 * local without conversion" bug that affected timesheet clock events.
 *
 * Background:
 *   The backend stores timestamps in UTC. When Pydantic / Mongo serialise a
 *   naive UTC datetime to JSON, the resulting string can lack a timezone
 *   designator (e.g. "2026-05-08T14:00:00" instead of "...Z"). Per the
 *   ECMAScript spec, datetime-only strings without a tz designator are
 *   parsed as LOCAL time by browsers — so 14:00 UTC ends up displayed at
 *   14:00 in the user's local timezone (effectively dropping the
 *   conversion). When that already-shifted value is re-serialised back to
 *   ISO and re-parsed (e.g. when pre-filling an <input type="datetime-local">
 *   for the edit modal), the bug compounds and the shift doubles
 *   (15:00 BST → 13:00 UTC after two passes).
 *
 *   Fix strategy: any timestamp coming from our backend MUST be treated
 *   as UTC. These helpers normalise that for the whole frontend.
 */

/**
 * Parse a backend timestamp string (or Date) and return a Date object.
 * If the string lacks a timezone designator we ASSUME UTC (since the
 * backend stores everything in UTC). Returns `null` for empty / invalid
 * input so callers can branch cleanly.
 */
export function parseBackendDate(value) {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  let s = String(value);
  // Already has a TZ designator (Z or ±HH:MM / ±HHMM) → trust it as-is.
  const hasTz = /(Z|[+-]\d{2}:?\d{2})$/i.test(s);
  if (!hasTz) {
    // No tz info → treat as UTC by appending Z.
    s = s + 'Z';
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Format a Date (or backend ISO string) for display via toLocaleTimeString.
 * Convenience wrapper that handles the UTC-without-Z gotcha automatically.
 *
 * Example: fmtTime("2026-05-08T14:00:00") in en-GB BST → "15:00".
 */
export function fmtTime(value, opts = { hour: '2-digit', minute: '2-digit' }) {
  const d = parseBackendDate(value);
  if (!d) return '';
  return d.toLocaleTimeString([], opts);
}

/**
 * Format a Date (or backend ISO string) for display as a date+time string
 * in the user's locale.
 */
export function fmtDateTime(value, opts) {
  const d = parseBackendDate(value);
  if (!d) return '';
  return opts ? d.toLocaleString([], opts) : d.toLocaleString();
}

/**
 * Convert a Date (or backend ISO string) into the value expected by
 * <input type="datetime-local"> — i.e. "YYYY-MM-DDTHH:MM" reflecting the
 * user's LOCAL timezone components.
 *
 * Critically, this uses `getFullYear/getMonth/getDate/getHours/getMinutes`
 * rather than `.toISOString().slice(0, 16)` — the latter returns UTC,
 * which inputs interpret as local, causing a silent shift.
 */
export function toDateTimeLocalInput(value) {
  const d = parseBackendDate(value);
  if (!d) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

/**
 * Inverse of toDateTimeLocalInput — given a "YYYY-MM-DDTHH:MM" string from
 * a datetime-local input (which represents LOCAL time), return a UTC ISO
 * string suitable for sending to the backend.
 *
 * `new Date("YYYY-MM-DDTHH:MM")` already parses as local; this helper just
 * makes intent explicit and validates input.
 */
export function dateTimeLocalToUtcIso(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}
